
import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { Alert } from '@/types';

interface AlertState {
  alerts: Alert[];
  filters: {
    level: string[];
    acknowledged: boolean | null;
    source: string[];
  };
  
  // Actions
  setAlerts: (alerts: Alert[]) => void;
  addAlert: (alert: Alert) => void;
  acknowledgeAlert: (id: string) => void;
  clearAlert: (id: string) => void;
  setFilters: (filters: Partial<AlertState['filters']>) => void;
  
  // Computed
  filteredAlerts: Alert[];
  unacknowledgedCount: number;
  criticalCount: number;
}

export const useAlertStore = create<AlertState>()(
  devtools((set, get) => ({
    alerts: [],
    filters: {
      level: [],
      acknowledged: null,
      source: [],
    },

    setAlerts: (alerts) => set({ alerts }),
    
    addAlert: (alert) => set((state) => ({
      alerts: [alert, ...state.alerts]
    })),
    
    acknowledgeAlert: (id) => set((state) => ({
      alerts: state.alerts.map(alert =>
        alert.id === id ? { ...alert, acknowledged: true } : alert
      )
    })),
    
    clearAlert: (id) => set((state) => ({
      alerts: state.alerts.filter(alert => alert.id !== id)
    })),
    
    setFilters: (newFilters) => set((state) => ({
      filters: { ...state.filters, ...newFilters }
    })),

    get filteredAlerts() {
      const { alerts, filters } = get();
      return alerts.filter(alert => {
        if (filters.level.length > 0 && !filters.level.includes(alert.level)) {
          return false;
        }
        if (filters.acknowledged !== null && alert.acknowledged !== filters.acknowledged) {
          return false;
        }
        if (filters.source.length > 0 && !filters.source.includes(alert.source)) {
          return false;
        }
        return true;
      });
    },

    get unacknowledgedCount() {
      return get().alerts.filter(alert => !alert.acknowledged).length;
    },

    get criticalCount() {
      return get().alerts.filter(alert => 
        alert.level === 'critical' && !alert.acknowledged
      ).length;
    },
  }), { name: 'alert-store' })
);
